import { ApolloClient } from '@apollo/client';
import type { ErrorLike } from '@apollo/client';
import type { useQuery } from '@apollo/client/react';
import { RetryLink } from '@apollo/client/link/retry';
import { DocumentNode, Kind } from 'graphql';

import { reportMykissClientError } from '@lib/errors/client';
import { isApolloNotFoundError } from '@lib/errors/errorUtils';
import { logger } from '@lib/logger/logger';
import { reportMykissServerError } from '@lib/errors/server';

export interface GraphqlResponse<Query> {
  notFound: boolean;
  error?: any;
  response?: ApolloClient.QueryResult<Query>;
}

type APIName = 'rutilus' | 'bass-or-not';

function getOperationName(query: DocumentNode): string | undefined {
  const operationDefinition = query.definitions.find(d => d.kind === Kind.OPERATION_DEFINITION);
  return operationDefinition ? operationDefinition.name?.value : undefined;
}

export function handleApolloErrorOnServer(
  error: ErrorLike,
  options: ApolloClient.QueryOptions,
  apiName: APIName,
): {
  notFound: boolean;
  error: ErrorLike;
} {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (Error.captureStackTrace) {
    Error.captureStackTrace(error);
  }
  reportMykissServerError(error, undefined, undefined, {
    variables: options.variables,
    operationName: getOperationName(options.query),
    apiName,
    // For ServerParseError/ServerError
    bodyText: (error as any)?.bodyText,
    statusCode: (error as any)?.statusCode,
    responseHeaders: (error as any)?.response?.headers,
  });

  // If the error is a 404 we return so that the page can return `notFound: true`,
  return { notFound: isApolloNotFoundError(error), error };
}

export function handleApolloErrorOnClient(
  error: ErrorLike,
  query: DocumentNode,
  options: useQuery.Options,
  apiName: APIName,
): void {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (Error.captureStackTrace) {
    Error.captureStackTrace(error);
  }
  reportMykissClientError(error, undefined, undefined, {
    variables: options.variables,
    operationName: getOperationName(query),
    apiName,
    // For ServerParseError/ServerError
    bodyText: (error as any)?.bodyText,
    statusCode: (error as any)?.statusCode,
    responseHeaders: (error as any)?.response?.headers,
  });
}

const NON_RETRYABLE_ERRORS = ['ServerParseError'];

export function getRetryLink(apiName: APIName, mode: 'server' | 'client' = 'client'): RetryLink {
  return new RetryLink({
    attempts: {
      max: 5,
      retryIf: (error, _operation) => {
        logger.warn({ error }, 'Error while retrying');
        logger.warn({ error }, error.message);
        if (!NON_RETRYABLE_ERRORS.includes(error.name)) {
          if (mode === 'server') {
            reportMykissServerError(
              new Error(`Retrying apollo query ${_operation.operationName}`),
              undefined,
              'info',
              {
                operation: _operation.operationName,
                errorMessage: error.message,
                apiName,
              },
            );
          } else {
            reportMykissClientError(
              new Error(`Retrying apollo query ${_operation.operationName}`),
              undefined,
              'info',
              {
                operation: _operation.operationName,
                errorMessage: error.message,
                apiName,
              },
            );
          }
        }
        return !!error;
      },
    },
  });
}
